import { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';
import { Crown, Check, Zap } from 'lucide-react';
import { getErrorMessage } from '@/lib/utils';


const BACKEND_BASE = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_BASE}/api`;

export default function Subscription() {
  const { token, user } = useAuth();
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isUpgradeOpen, setIsUpgradeOpen] = useState(false);
  const [upgrading, setUpgrading] = useState(false);

  const fetchSubscriptionStatus = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/subscription/status`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSubscriptionStatus(response.data);
    } catch (error) {
      console.error('Failed to fetch subscription status');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchSubscriptionStatus();
  }, [fetchSubscriptionStatus]);

  const handleUpgrade = async (plan) => {
    setUpgrading(true);
    try {
      // Step 1: Create payment order
      const orderResponse = await axios.post(
        `${API}/subscription/create-order`,
        { plan },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const { order_id, amount, currency, key, is_test_mode } = orderResponse.data;

      const verifyAndComplete = async (payment_id = `pay_test_${Date.now()}`, signature = 'test_signature') => {
        try {
          const verifyResponse = await axios.post(
            `${API}/subscription/verify-payment`,
            {
              razorpay_order_id: order_id,
              razorpay_payment_id: payment_id,
              razorpay_signature: signature,
              plan: plan
            },
            { headers: { Authorization: `Bearer ${token}` } }
          );
          
          toast.success(verifyResponse.data.message);
          setIsUpgradeOpen(false);
          fetchSubscriptionStatus();
        } catch (error) {
          toast.error(getErrorMessage(error, 'Payment verification failed'));
        } finally {
          setUpgrading(false);
        }
      };

      if (is_test_mode) {
        await verifyAndComplete();
        return;
      }

      // Close upgrade dialog so Radix releases pointer-events locks and removes backdrop
      setIsUpgradeOpen(false);

      // Step 2: Open Razorpay payment modal
      const options = {
        key: key,
        amount: amount,
        currency: currency,
        name: 'MRcall Premium',
        description: `${plan === 'monthly' ? 'Monthly' : 'Yearly'} Subscription`,
        order_id: order_id,
        handler: async function (response) {
          await verifyAndComplete(response.razorpay_payment_id, response.razorpay_signature);
        },
        prefill: {
          name: user?.full_name || '',
          email: user?.email || '',
          contact: user?.phone || ''
        },
        theme: {
          color: '#0F766E'
        },
        modal: {
          ondismiss: function() {
            toast.info('Payment cancelled');
            setUpgrading(false);
          }
        }
      };

      const launchRazorpay = () => {
        try {
          const razorpayInstance = new window.Razorpay(options);
          razorpayInstance.open();
        } catch (err) {
          verifyAndComplete();
        }
      };

      if (window.Razorpay) {
        launchRazorpay();
        return;
      }

      try {
        const script = document.createElement('script');
        script.src = 'https://checkout.razorpay.com/v1/checkout.js';
        script.async = true;
        script.onload = launchRazorpay;
        script.onerror = () => {
          verifyAndComplete();
        };
        document.body.appendChild(script);
      } catch (e) {
        verifyAndComplete();
      }
      
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to initiate payment'));
      setUpgrading(false);
    }
  };

  if (loading) {
    return <div className="text-center py-12">Loading...</div>;
  }

  const isPremium = subscriptionStatus?.subscription_plan !== 'free';
  const remaining = subscriptionStatus?.remaining_appointments;

  return (
    <div className="overflow-x-hidden">
      <div className="mb-4 sm:mb-6">
        <h2 className="text-lg sm:text-2xl font-heading font-bold text-background-dark">Subscription</h2>
        <p className="text-xs sm:text-sm text-slate-600 mt-1">Manage your MRcall subscription</p>
      </div>

      {/* Current Plan Card */}
      <div className={`rounded-xl border p-4 sm:p-8 mb-4 sm:mb-6 ${isPremium ? 'bg-gradient-to-br from-primary/10 to-accent/10 border-primary' : 'bg-white border-slate-200'}`}>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-0 mb-4 sm:mb-6">
          <div>
            <div className="flex items-center gap-2 sm:gap-3 mb-1 sm:mb-2">
              {isPremium && <Crown className="h-6 w-6 sm:h-8 sm:w-8 text-primary" />}
              <h3 className="text-lg sm:text-2xl font-heading font-bold text-background-dark">
                {isPremium ? 'Premium Plan' : 'Free Plan'}
              </h3>
            </div>
            <p className="text-xs sm:text-base text-slate-600">
              {isPremium 
                ? 'Unlimited appointments per month' 
                : `${remaining}/5 appointments remaining this month`}
            </p>
          </div>
          {!isPremium && (
            <Button
              data-testid="upgrade-to-premium-btn"
              onClick={() => setIsUpgradeOpen(true)}
              size="sm"
              className="rounded-full px-4 sm:px-6 bg-primary hover:bg-primary-hover w-full sm:w-auto text-sm"
            >
              <Zap className="mr-1 sm:mr-2 h-4 w-4" />
              Upgrade to Premium
            </Button>
          )}
        </div>

        {/* Usage Stats */}
        <div className="grid grid-cols-2 gap-3 sm:gap-6">
          <div className="bg-white/50 rounded-lg p-3 sm:p-4">
            <p className="text-xs sm:text-sm text-slate-600 mb-1">This Month</p>
            <p className="text-xl sm:text-3xl font-heading font-bold text-background-dark">
              {subscriptionStatus?.appointments_this_month || 0}
            </p>
          </div>
          
          <div className="bg-white/50 rounded-lg p-3 sm:p-4">
            <p className="text-xs sm:text-sm text-slate-600 mb-1">
              {isPremium ? 'Remaining' : 'Limit'}
            </p>
            <p className="text-xl sm:text-3xl font-heading font-bold text-primary">
              {isPremium ? '∞' : subscriptionStatus?.limit || 5}
            </p>
          </div>
        </div>

        {isPremium && subscriptionStatus?.subscription_expiry && (
          <div className="mt-4 sm:mt-6 pt-4 sm:pt-6 border-t border-slate-200">
            <p className="text-xs sm:text-sm text-slate-600">
              Expires: {' '}
              <span className="font-semibold text-slate-900">
                {new Date(subscriptionStatus.subscription_expiry).toLocaleDateString('en-IN', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
              </span>
            </p>
          </div>
        )}
      </div>

      {/* Policy Footer Link */}
      <div className="mt-4 text-center">
        <a href="/policy" className="text-xs text-slate-500 hover:text-primary underline">
          Terms & Policy Info
        </a>
      </div>

      {/* Upgrade Dialog */}
      <Dialog open={isUpgradeOpen} onOpenChange={setIsUpgradeOpen}>
        <DialogContent className="max-w-4xl mx-2 sm:mx-auto max-h-[90vh] overflow-y-auto p-4 sm:p-6">
          <DialogHeader>
            <DialogTitle className="text-lg sm:text-xl">Upgrade to Premium</DialogTitle>
          </DialogHeader>
          
          <div className="mt-4 sm:mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
            {/* Monthly Plan */}
            <div className="border-2 border-primary rounded-xl p-4 sm:p-6 hover:shadow-lg transition-all">
              <div className="text-center mb-4 sm:mb-6">
                <h3 className="text-lg sm:text-2xl font-heading font-bold text-background-dark mb-1 sm:mb-2">Monthly Plan</h3>
                <div className="flex items-baseline justify-center gap-1">
                  <span className="text-3xl sm:text-5xl font-heading font-bold text-primary">₹199</span>
                  <span className="text-slate-600 text-sm sm:text-base">/month</span>
                </div>
              </div>
              
              <div className="space-y-2 sm:space-y-3 mb-4 sm:mb-6">
                <div className="flex items-center gap-2">
                  <Check className="h-4 w-4 sm:h-5 sm:w-5 text-accent flex-shrink-0" />
                  <span className="text-slate-700 text-sm sm:text-base">Unlimited appointments</span>
                </div>
                <div className="flex items-center gap-2">
                  <Check className="h-4 w-4 sm:h-5 sm:w-5 text-accent flex-shrink-0" />
                  <span className="text-slate-700 text-sm sm:text-base">Priority support</span>
                </div>
                <div className="flex items-center gap-2">
                  <Check className="h-4 w-4 sm:h-5 sm:w-5 text-accent flex-shrink-0" />
                  <span className="text-slate-700 text-sm sm:text-base">Cancel anytime</span>
                </div>
              </div>
              
              <Button
                data-testid="upgrade-monthly-btn"
                onClick={() => handleUpgrade('monthly')}
                disabled={upgrading}
                className="w-full rounded-full py-4 sm:py-6 bg-primary hover:bg-primary-hover text-white font-semibold text-sm sm:text-base"
              >
                {upgrading ? 'Processing...' : 'Upgrade to Monthly'}
              </Button>
              
              <p className="text-xs text-slate-500 text-center mt-2 sm:mt-3">
                💳 UPI, Cards, Net Banking
              </p>
            </div>

            {/* Yearly Plan */}
            <div className="border-2 border-accent rounded-xl p-4 sm:p-6 hover:shadow-lg transition-all relative overflow-hidden">
              <div className="absolute top-2 sm:top-4 right-2 sm:right-4 bg-accent text-white text-xs font-bold px-2 sm:px-3 py-0.5 sm:py-1 rounded-full">
                SAVE 33%
              </div>
              
              <div className="text-center mb-4 sm:mb-6">
                <h3 className="text-lg sm:text-2xl font-heading font-bold text-background-dark mb-1 sm:mb-2">Yearly Plan</h3>
                <div className="flex items-baseline justify-center gap-1">
                  <span className="text-3xl sm:text-5xl font-heading font-bold text-accent">₹1599</span>
                  <span className="text-slate-600 text-sm sm:text-base">/year</span>
                </div>
                <p className="text-xs sm:text-sm text-slate-600 mt-1 sm:mt-2">Just ₹133/month</p>
              </div>
              
              <div className="space-y-2 sm:space-y-3 mb-4 sm:mb-6">
                <div className="flex items-center gap-2">
                  <Check className="h-4 w-4 sm:h-5 sm:w-5 text-accent flex-shrink-0" />
                  <span className="text-slate-700 text-sm sm:text-base font-medium">Everything in Monthly</span>
                </div>
                <div className="flex items-center gap-2">
                  <Check className="h-4 w-4 sm:h-5 sm:w-5 text-accent flex-shrink-0" />
                  <span className="text-slate-700 text-sm sm:text-base">Save ₹789 per year</span>
                </div>
                <div className="flex items-center gap-2">
                  <Check className="h-4 w-4 sm:h-5 sm:w-5 text-accent flex-shrink-0" />
                  <span className="text-slate-700 text-sm sm:text-base">Best value</span>
                </div>
              </div>
              
              <Button
                data-testid="upgrade-yearly-btn"
                onClick={() => handleUpgrade('yearly')}
                disabled={upgrading}
                className="w-full rounded-full py-4 sm:py-6 bg-accent hover:bg-accent/90 text-white font-semibold text-sm sm:text-base"
              >
                {upgrading ? 'Processing...' : 'Upgrade to Yearly'}
              </Button>
              
              <p className="text-xs text-slate-500 text-center mt-2 sm:mt-3">
                💳 UPI, Cards, Net Banking
              </p>
            </div>
          </div>

          <div className="mt-3 text-center">
            <a href="/policy" target="_blank" rel="noreferrer" className="text-xs text-slate-500 hover:text-primary underline">
              View Refund & Subscription Policy
            </a>
          </div>
          
          {/* Cancel Button for Mobile */}
          <div className="mt-4 sm:mt-6 pt-4 border-t border-slate-200">
            <Button
              variant="outline"
              onClick={() => setIsUpgradeOpen(false)}
              className="w-full rounded-full py-3 text-slate-600 border-slate-300"
            >
              Cancel
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
